import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import VehicleInput from '../components/VehicleInput';
import { INDIAN_STATES } from '../utils/states';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Fill Later'];

const RegisterDetails = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referralValidating, setReferralValidating] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [referralMessage, setReferralMessage] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    photo: '',
    bloodGroup: '',
    digitalHealthCard: '',
    arogyasriCard: '',
    ayushmanBharatCard: '',
    insuranceCard: '',
    healthProfileFiles: [],
    altContact: '',
    phoneNumber: '',
    vehicle1Name: '',
    vehicle1Number: '',
    vehicle2Name: '',
    vehicle2Number: '',
    vehicle3Name: '',
    vehicle3Number: '',
    familyMembers: [],
    addressState: '',
    addressPincode: '',
    addressArea: '',
    referralCode: '',
    agreeToTerms: false,
  });

  useEffect(() => {
    if (!user || !user._id) {
      navigate('/register');
    } else if (user.is_registered && user.is_paid) {
      navigate('/dashboard');
    } else if (user.is_registered && !user.is_paid) {
      navigate('/payment');
    }
  }, [user, navigate]);

  const handlePhotoUpload = async (e, isFamilyMember = false, index = null) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('photo', file);

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/upload-photo`,
        formDataUpload,
        { withCredentials: true }
      );

      if (isFamilyMember && index !== null) {
        const updatedFamily = [...formData.familyMembers];
        updatedFamily[index].photo = data.photo_url;
        setFormData({ ...formData, familyMembers: updatedFamily });
      } else {
        setFormData({ ...formData, photo: data.photo_url });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to upload photo');
    }
  };

  const handleHealthCardUpload = async (e, isFamilyMember = false, index = null) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/upload-health-card`,
        formDataUpload,
        { withCredentials: true }
      );

      if (isFamilyMember && index !== null) {
        const updatedFamily = [...formData.familyMembers];
        updatedFamily[index].digitalHealthCard = data.file_url;
        setFormData({ ...formData, familyMembers: updatedFamily });
      } else {
        setFormData({ ...formData, digitalHealthCard: data.file_url });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to upload health card');
    }
  };

  const handleDocUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/upload-health-card`,
        formDataUpload,
        { withCredentials: true }
      );
      setFormData({ ...formData, [fieldName]: data.file_url });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to upload document');
    }
  };

  const handleHealthProfileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const remaining = 3 - formData.healthProfileFiles.length;
    if (remaining <= 0) {
      setError('Maximum 3 health profile files allowed');
      return;
    }
    const toUpload = files.slice(0, remaining);
    const uploaded = [];
    for (const file of toUpload) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/profile/upload-health-card`,
          fd,
          { withCredentials: true }
        );
        uploaded.push(data.file_url);
      } catch (err) {
        setError('Failed to upload a health profile file');
      }
    }
    if (uploaded.length) {
      setFormData(prev => ({ ...prev, healthProfileFiles: [...prev.healthProfileFiles, ...uploaded] }));
    }
  };

  const removeHealthProfileFile = (index) => {
    setFormData(prev => ({
      ...prev,
      healthProfileFiles: prev.healthProfileFiles.filter((_, i) => i !== index),
    }));
  };

  const validateReferralCode = async (code) => {
    if (!code.trim()) {
      setReferralValid(null);
      setReferralMessage('');
      return;
    }

    setReferralValidating(true);
    try {
      const formDataValidate = new FormData();
      formDataValidate.append('referral_code', code);

      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/referral/validate`,
        formDataValidate,
        { withCredentials: true }
      );

      setReferralValid(true);
      setReferralMessage(`Valid! Referred by ${data.referrer_name}. You'll get ₹25 discount!`);
    } catch (err) {
      setReferralValid(false);
      setReferralMessage('Invalid referral code');
    } finally {
      setReferralValidating(false);
    }
  };

  const addFamilyMember = () => {
    if (formData.familyMembers.length < 3) {
      setFormData({
        ...formData,
        familyMembers: [...formData.familyMembers, { name: '', bloodGroup: '', digitalHealthCard: '', photo: '', phoneNumber: '' }],
      });
    }
  };

  const removeFamilyMember = (index) => {
    const updated = formData.familyMembers.filter((_, i) => i !== index);
    setFormData({ ...formData, familyMembers: updated });
  };

  const updateFamilyMember = (index, field, value) => {
    const updated = [...formData.familyMembers];
    updated[index][field] = value;
    setFormData({ ...formData, familyMembers: updated });
  };

  const handleNext = () => {
    if (step === 1 && !formData.name) {
      setError('Name is required');
      return;
    }
    if (step === 2 && !formData.vehicle1Number) {
      setError('At least one vehicle number is required');
      return;
    }
    if (step === 4 && (!formData.addressState || !formData.addressPincode || !formData.addressArea)) {
      setError('All address fields are required');
      return;
    }
    setError('');
    setStep(step + 1);
  };

  const handleBack = () => {
    setError('');
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!formData.agreeToTerms) {
      setError('You must agree to the terms and conditions');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const submitFormData = new FormData();
      submitFormData.append('name', formData.name);
      submitFormData.append('photo', formData.photo);
      submitFormData.append('blood_group', formData.bloodGroup || 'Fill Later');
      submitFormData.append('digital_health_card', formData.digitalHealthCard);
      submitFormData.append('arogyasri_card', formData.arogyasriCard);
      submitFormData.append('ayushman_bharat_card', formData.ayushmanBharatCard);
      submitFormData.append('insurance_card', formData.insuranceCard);
      submitFormData.append('health_profile_files', JSON.stringify(formData.healthProfileFiles));
      submitFormData.append('alt_contact', formData.altContact);
      submitFormData.append('phone_number', formData.phoneNumber);
      submitFormData.append('vehicle1_name', formData.vehicle1Name);
      submitFormData.append('vehicle1_number', formData.vehicle1Number);
      submitFormData.append('vehicle2_name', formData.vehicle2Name);
      submitFormData.append('vehicle2_number', formData.vehicle2Number);
      submitFormData.append('vehicle3_name', formData.vehicle3Name);
      submitFormData.append('vehicle3_number', formData.vehicle3Number);
      submitFormData.append('family_members', JSON.stringify(formData.familyMembers));
      submitFormData.append('address_state', formData.addressState);
      submitFormData.append('address_pincode', formData.addressPincode);
      submitFormData.append('address_area', formData.addressArea);
      submitFormData.append('referral_code_used', formData.referralCode);

      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/complete-registration`,
        submitFormData,
        { withCredentials: true }
      );

      updateUser(data);
      navigate('/payment');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-medium">Individual Details</h3>

            <div>
              <label className="block text-sm font-medium mb-2">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="individual-name-input"
                className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-lg p-3 text-white outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Photo</label>
              <div className="flex gap-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handlePhotoUpload(e)}
                  data-testid="individual-photo-upload"
                  className="flex-1 bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white"
                />
                <label className="px-4 py-3 bg-[#0A84FF] hover:bg-[#0070E0] rounded-lg cursor-pointer transition-all flex items-center gap-2 text-sm font-medium whitespace-nowrap">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Camera
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handlePhotoUpload(e)}
                    className="hidden"
                  />
                </label>
              </div>
              {formData.photo && (
                <img src={formData.photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${formData.photo}` : formData.photo} alt="Preview" className="mt-4 w-32 h-32 object-cover rounded-lg" />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Blood Group</label>
              <select
                value={formData.bloodGroup}
                onChange={(e) => setFormData({ ...formData, bloodGroup: e.target.value })}
                data-testid="individual-blood-group-select"
                className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-lg p-3 text-white outline-none"
              >
                <option value="">Select Blood Group</option>
                {BLOOD_GROUPS.map((bg) => (
                  <option key={bg} value={bg}>
                    {bg}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Digital Health Card (Optional)</label>
              <div className="flex gap-3">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleDocUpload(e, 'digitalHealthCard')}
                  data-testid="individual-health-card-upload"
                  className="flex-1 bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white"
                />
                <label className="px-4 py-3 bg-[#0A84FF] hover:bg-[#0070E0] rounded-lg cursor-pointer transition-all flex items-center gap-2 text-sm font-medium whitespace-nowrap">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Camera
                  <input type="file" accept="image/*" capture="environment" onChange={(e) => handleDocUpload(e, 'digitalHealthCard')} className="hidden" />
                </label>
              </div>
              {formData.digitalHealthCard && <span className="text-[#32D74B] text-sm mt-1 block">Uploaded</span>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Arogyasri Card (Optional)</label>
              <div className="flex gap-3">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleDocUpload(e, 'arogyasriCard')}
                  data-testid="individual-arogyasri-upload"
                  className="flex-1 bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white"
                />
                <label className="px-4 py-3 bg-[#0A84FF] hover:bg-[#0070E0] rounded-lg cursor-pointer transition-all flex items-center gap-2 text-sm font-medium whitespace-nowrap">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Camera
                  <input type="file" accept="image/*" capture="environment" onChange={(e) => handleDocUpload(e, 'arogyasriCard')} className="hidden" />
                </label>
              </div>
              {formData.arogyasriCard && <span className="text-[#32D74B] text-sm mt-1 block">Uploaded</span>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Ayushman Bharat Card (Optional)</label>
              <div className="flex gap-3">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleDocUpload(e, 'ayushmanBharatCard')}
                  data-testid="individual-ayushman-upload"
                  className="flex-1 bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white"
                />
                <label className="px-4 py-3 bg-[#0A84FF] hover:bg-[#0070E0] rounded-lg cursor-pointer transition-all flex items-center gap-2 text-sm font-medium whitespace-nowrap">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Camera
                  <input type="file" accept="image/*" capture="environment" onChange={(e) => handleDocUpload(e, 'ayushmanBharatCard')} className="hidden" />
                </label>
              </div>
              {formData.ayushmanBharatCard && <span className="text-[#32D74B] text-sm mt-1 block">Uploaded</span>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Insurance Document (Optional)</label>
              <div className="flex gap-3">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleDocUpload(e, 'insuranceCard')}
                  data-testid="individual-insurance-upload"
                  className="flex-1 bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white"
                />
                <label className="px-4 py-3 bg-[#0A84FF] hover:bg-[#0070E0] rounded-lg cursor-pointer transition-all flex items-center gap-2 text-sm font-medium whitespace-nowrap">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Camera
                  <input type="file" accept="image/*" capture="environment" onChange={(e) => handleDocUpload(e, 'insuranceCard')} className="hidden" />
                </label>
              </div>
              {formData.insuranceCard && <span className="text-[#32D74B] text-sm mt-1 block">Uploaded</span>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Health Profile (Optional - Max 3 files)</label>
              {formData.healthProfileFiles.length < 3 && (
                <input
                  type="file"
                  accept="image/*,.pdf"
                  multiple
                  onChange={handleHealthProfileUpload}
                  data-testid="individual-health-profile-upload"
                  className="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white"
                />
              )}
              {formData.healthProfileFiles.length > 0 && (
                <div className="mt-2 space-y-2">
                  {formData.healthProfileFiles.map((url, i) => (
                    <div key={i} className="flex items-center justify-between bg-black/30 border border-white/10 rounded-lg px-3 py-2">
                      <span className="text-[#32D74B] text-sm truncate flex-1">File {i + 1} uploaded</span>
                      <button type="button" onClick={() => removeHealthProfileFile(i)} className="ml-2 text-[#FF3B30] hover:text-red-400 text-sm font-medium">Remove</button>
                    </div>
                  ))}
                  <p className="text-[#A1A1A6] text-xs">{formData.healthProfileFiles.length}/3 files uploaded</p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Alternative Contact Number (Optional)</label>
              <input
                type="tel"
                value={formData.altContact}
                onChange={(e) => setFormData({ ...formData, altContact: e.target.value })}
                data-testid="individual-alt-contact-input"
                placeholder="+91 XXXXXXXXXX"
                className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-lg p-3 text-white outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Applicant Phone Number (Optional - Private)</label>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                data-testid="individual-phone-number-input"
                placeholder="+91 XXXXXXXXXX"
                className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-lg p-3 text-white outline-none"
              />
              <p className="text-[#A1A1A6] text-xs mt-1">This number will NOT be visible in vehicle search results</p>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-medium">Vehicle Details</h3>

            <div className="space-y-4">
              <div className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10">
                <h4 className="font-medium mb-4">Vehicle 1 *</h4>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Vehicle Name"
                    value={formData.vehicle1Name}
                    onChange={(e) => setFormData({ ...formData, vehicle1Name: e.target.value })}
                    data-testid="vehicle1-name-input"
                    className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] rounded-lg p-3 text-white outline-none"
                  />
                  <VehicleInput
                    value={formData.vehicle1Number}
                    onChange={(val) => setFormData({ ...formData, vehicle1Number: val })}
                    placeholder="Vehicle Number *"
                    required
                    dataTestId="vehicle1-number-input"
                  />
                </div>
              </div>

              <div className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10">
                <h4 className="font-medium mb-4">Vehicle 2 (Optional)</h4>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Vehicle Name"
                    value={formData.vehicle2Name}
                    onChange={(e) => setFormData({ ...formData, vehicle2Name: e.target.value })}
                    data-testid="vehicle2-name-input"
                    className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] rounded-lg p-3 text-white outline-none"
                  />
                  <VehicleInput
                    value={formData.vehicle2Number}
                    onChange={(val) => setFormData({ ...formData, vehicle2Number: val })}
                    placeholder="Vehicle Number"
                    dataTestId="vehicle2-number-input"
                  />
                </div>
              </div>

              <div className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10">
                <h4 className="font-medium mb-4">Vehicle 3 (Optional)</h4>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Vehicle Name"
                    value={formData.vehicle3Name}
                    onChange={(e) => setFormData({ ...formData, vehicle3Name: e.target.value })}
                    data-testid="vehicle3-name-input"
                    className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] rounded-lg p-3 text-white outline-none"
                  />
                  <VehicleInput
                    value={formData.vehicle3Number}
                    onChange={(val) => setFormData({ ...formData, vehicle3Number: val })}
                    placeholder="Vehicle Number"
                    dataTestId="vehicle3-number-input"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-medium">Family Details</h3>
              {formData.familyMembers.length < 3 && (
                <button
                  onClick={addFamilyMember}
                  data-testid="add-family-member-button"
                  className="px-4 py-2 bg-[#FF3B30] hover:bg-[#E3242B] rounded-full text-sm"
                >
                  Add Member
                </button>
              )}
            </div>

            {formData.familyMembers.length === 0 ? (
              <p className="text-[#A1A1A6] text-center py-12">No family members added yet. Click "Add Member" to get started.</p>
            ) : (
              <div className="space-y-4">
                {formData.familyMembers.map((member, index) => (
                  <div key={index} className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium">Member {index + 1}</h4>
                      <button
                        onClick={() => removeFamilyMember(index)}
                        data-testid={`remove-family-member-${index}-button`}
                        className="text-[#FF453A] text-sm hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="space-y-4">
                      <input
                        type="text"
                        placeholder="Name"
                        value={member.name}
                        onChange={(e) => updateFamilyMember(index, 'name', e.target.value)}
                        data-testid={`family-member-${index}-name-input`}
                        className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] rounded-lg p-3 text-white outline-none"
                      />
                      <select
                        value={member.bloodGroup}
                        onChange={(e) => updateFamilyMember(index, 'bloodGroup', e.target.value)}
                        data-testid={`family-member-${index}-blood-group-select`}
                        className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] rounded-lg p-3 text-white outline-none"
                      >
                        <option value="">Select Blood Group</option>
                        {BLOOD_GROUPS.map((bg) => (
                          <option key={bg} value={bg}>
                            {bg}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Digital Health Card Number (Optional)"
                        value={member.digitalHealthCard || ''}
                        onChange={(e) => updateFamilyMember(index, 'digitalHealthCard', e.target.value)}
                        data-testid={`family-member-${index}-health-card-input`}
                        className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] rounded-lg p-3 text-white outline-none"
                      />
                      <label className="block text-sm font-medium mb-1 text-gray-300">Choose Family Member Photo</label>
                      <div className="flex gap-3">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handlePhotoUpload(e, true, index)}
                          data-testid={`family-member-${index}-photo-upload`}
                          className="flex-1 bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white"
                          title="Choose Family Member Photo"
                        />
                        <label className="px-4 py-3 bg-[#0A84FF] hover:bg-[#0070E0] rounded-lg cursor-pointer transition-all flex items-center gap-2 text-sm font-medium whitespace-nowrap">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Camera
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => handlePhotoUpload(e, true, index)}
                            className="hidden"
                          />
                        </label>
                      </div>
                      {member.photo && (
                        <img src={member.photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${member.photo}` : member.photo} alt="Preview" className="w-24 h-24 object-cover rounded-lg" />
                      )}
                      <div>
                        <input
                          type="tel"
                          placeholder="Applicant Phone Number (Optional - Private)"
                          value={member.phoneNumber || ''}
                          onChange={(e) => updateFamilyMember(index, 'phoneNumber', e.target.value)}
                          data-testid={`family-member-${index}-phone-input`}
                          className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] rounded-lg p-3 text-white outline-none"
                        />
                        <p className="text-[#A1A1A6] text-xs mt-1">Not visible in search results</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-medium">Address Details</h3>
            
            <div className="bg-[#FF9F0A]/10 border border-[#FF9F0A]/20 rounded-lg p-4 mb-6">
              <p className="text-[#FF9F0A] text-sm">
                <strong>Privacy Notice:</strong> Your address information is completely private and will NOT be visible to anyone searching by vehicle number. This information is only accessible to MyGaadiNumber for emergency contact purposes.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">State *</label>
              <select
                value={formData.addressState}
                onChange={(e) => setFormData({ ...formData, addressState: e.target.value })}
                data-testid="address-state-select"
                className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-lg p-3 text-white outline-none"
                required
              >
                <option value="">Select State</option>
                {INDIAN_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Pincode *</label>
              <input
                type="text"
                value={formData.addressPincode}
                onChange={(e) => setFormData({ ...formData, addressPincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                data-testid="address-pincode-input"
                placeholder="Enter 6-digit pincode"
                maxLength={6}
                className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-lg p-3 text-white outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Area/Locality *</label>
              <textarea
                value={formData.addressArea}
                onChange={(e) => setFormData({ ...formData, addressArea: e.target.value })}
                data-testid="address-area-input"
                placeholder="Enter your area, street, locality"
                rows={3}
                className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-lg p-3 text-white outline-none resize-none"
                required
              />
            </div>
          </motion.div>
        );

      case 5:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-medium">Referral & Payment</h3>

            <div className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10">
              <h4 className="font-medium mb-2">Referral Code (Optional)</h4>
              <p className="text-[#A1A1A6] text-sm mb-4">Get ₹25 discount with a referral code</p>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter referral code"
                  value={formData.referralCode}
                  onChange={(e) => {
                    setFormData({ ...formData, referralCode: e.target.value });
                    validateReferralCode(e.target.value);
                  }}
                  data-testid="referral-code-input"
                  className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] rounded-lg p-3 text-white outline-none pr-10"
                />
                {referralValidating && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
              {referralMessage && (
                <p className={`text-sm mt-2 ${referralValid ? 'text-[#32D74B]' : 'text-[#FF453A]'}`} data-testid="referral-validation-message">
                  {referralMessage}
                </p>
              )}
            </div>

            <div className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10">
              <h4 className="font-medium mb-2">Payment Details</h4>
              <div className="flex justify-between items-center py-2">
                <span className="text-[#A1A1A6]">Lifetime Subscription</span>
                <span className="text-xl font-medium">₹150</span>
              </div>
              {referralValid && formData.referralCode && (
                <div className="flex justify-between items-center py-2 text-[#32D74B]">
                  <span>Referral Discount</span>
                  <span>-₹25</span>
                </div>
              )}
              <div className="border-t border-white/10 mt-4 pt-4 flex justify-between items-center">
                <span className="font-medium">Total Amount</span>
                <span className="text-2xl font-medium text-[#FF3B30]">
                  ₹{referralValid && formData.referralCode ? '125' : '150'}
                </span>
              </div>
            </div>

            <div className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.agreeToTerms}
                  onChange={(e) => setFormData({ ...formData, agreeToTerms: e.target.checked })}
                  data-testid="consent-checkbox"
                  className="mt-1"
                />
                <span className="text-sm text-[#A1A1A6]">
                  I agree that my details (except address) will be shared in case of emergency. I understand that MyGaadiNumber is not
                  responsible for any false information I provide. I have read and agree to the{' '}
                  <a href="/terms" target="_blank" className="text-[#FF3B30] hover:underline">
                    Terms & Conditions
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" target="_blank" className="text-[#FF3B30] hover:underline">
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black py-24 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="bg-[#121214] border border-white/10 rounded-2xl p-8">
          <div className="mb-8">
            <div className="flex justify-between mb-4">
              {[1, 2, 3, 4, 5].map((s) => (
                <div
                  key={s}
                  className={`h-2 flex-1 mx-1 rounded-full ${
                    s <= step ? 'bg-[#FF3B30]' : 'bg-white/10'
                  }`}
                />
              ))}
            </div>
            <p className="text-[#A1A1A6] text-sm text-center">
              Step {step} of 5
            </p>
          </div>

          <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>

          {error && (
            <div className="mt-6 bg-[#FF453A]/10 border border-[#FF453A]/20 rounded-lg p-3 text-[#FF453A] text-sm" data-testid="registration-error-message">
              {error}
            </div>
          )}

          <div className="flex gap-4 mt-8">
            {step > 1 && (
              <button
                onClick={handleBack}
                data-testid="back-button"
                className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-full font-medium transition-all"
              >
                Back
              </button>
            )}
            {step < 5 ? (
              <button
                onClick={handleNext}
                data-testid="next-button"
                className="flex-1 px-6 py-3 bg-[#FF3B30] hover:bg-[#E3242B] rounded-full font-medium transition-all"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                data-testid="submit-registration-button"
                className="flex-1 px-6 py-3 bg-[#FF3B30] hover:bg-[#E3242B] rounded-full font-medium transition-all disabled:opacity-50"
              >
                {loading ? 'Completing...' : 'Complete Registration'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterDetails;
